<script setup lang="ts">
import type { UiIconName } from "@/components/UiIcon.vue";

import { computed, useId } from "vue";

import UiIcon from "@/components/UiIcon.vue";

/**
 * 글자 없는 아이콘 버튼(`.btn.ui-icon`, 70차 UXE-04). 행 삭제 글자 `×` · 편집 `✎` · 손수 닫기를 한 모양으로 맞춘다.
 * - 이름이 보이지 않으므로 `label` 이 필수다 — `aria-label` 과 마우스 `title` 에 함께 붙는다.
 * - 변형: default = 흰 면 + 회색 선(보조 동작), quiet = 선 없는 회색(닫기 · 펼치기처럼 위험하지 않은 반복 동작),
 *   danger = 빨강(단일 삭제), danger-quiet = 평소 회색이고 가리키거나 키보드로 닿을 때만 빨강(행마다 반복되는 삭제).
 * - 잠김 · 막힘은 LoadingButton 과 같이 `disabled` 가 아니라 `aria-disabled="true"` 로 둔다(포커스를 잃지 않는다). 그동안 누름은 보내지 않는다.
 *   막힌 이유(`blockedReason`)는 칸이 좁은 아이콘 버튼이라 화면 읽기용 숨은 글자(aria-describedby) + title 로 둔다
 *   (LoadingButton `reasonPlacement="sr"` 과 같은 방식). 이유를 눈에 보여야 하면 글자 있는 LoadingButton 을 쓴다.
 * - 크기: md 36px(카드 머리 · 도구줄), sm 30px(표 칸 · 행 안).
 */
const props = withDefaults(
  defineProps<{
    /** 아이콘 이름 */
    icon: UiIconName;
    /** 접근 이름(aria-label 겸 title) */
    label: string;
    /** default · quiet · danger · danger-quiet(행 안 반복 삭제) */
    variant?: "default" | "quiet" | "danger" | "danger-quiet";
    /** md 36 · sm 30(표 칸 · 행 안) */
    size?: "sm" | "md";
    /** 잠김(포커스는 유지) */
    disabled?: boolean;
    /** 막힌 이유. 주면 잠그고 화면 읽기 설명 + title 로 이유를 둔다 */
    blockedReason?: string;
    /** 버튼 type */
    type?: "button" | "submit";
  }>(),
  {
    variant: "default",
    size: "md",
    disabled: false,
    blockedReason: undefined,
    type: "button",
  },
);

const emit = defineEmits<{
  /** 누름(잠김 · 막힘일 때는 보내지 않는다) */
  click: [event: MouseEvent];
}>();

const VARIANT_CLASSES: Record<NonNullable<typeof props.variant>, string[]> = {
  default: ["ghost"],
  quiet: ["ghost", "is-plain"],
  danger: ["ui-danger"],
  "danger-quiet": ["ghost", "is-quiet"],
};

const reasonId = useId();

const blocked = computed(() => props.disabled || !!props.blockedReason);
const classes = computed(() => [
  ...VARIANT_CLASSES[props.variant],
  `is-${props.size}`,
]);
/** sm 칸(30) 안에서는 16px, md(36)는 20px 아이콘 — 글자 버튼의 글자 높이와 시각 무게를 맞춘다 */
const iconSize = computed(() => (props.size === "sm" ? 16 : 20));

function onClick(event: MouseEvent) {
  if (blocked.value) {
    event.preventDefault();
    return;
  }
  emit("click", event);
}
</script>

<template>
  <button
    :type="type"
    class="btn ui-icon"
    :class="classes"
    :aria-label="label"
    :title="blockedReason || label"
    :aria-disabled="blocked ? 'true' : undefined"
    :aria-describedby="blockedReason ? reasonId : undefined"
    @click="onClick"
  >
    <UiIcon :name="icon" :size="iconSize" />
    <!-- hidden 이라 버튼 이름에서는 빠지고 aria-describedby 설명으로만 읽힌다 -->
    <span v-if="blockedReason" :id="reasonId" hidden>{{ blockedReason }}</span>
  </button>
</template>
